import { NavLink } from "react-router-dom";
import { useTranslate } from "ra-core";
import useViewerParams from "shared/hooks/useViewerParams";
import { useSnapshot } from "valtio";
import viewerState from "stores/viewer";
import useMarkers from "shared/hooks/useMarkers";
import { markers } from "shared/utils/recursive";

export const MODES = {
  QUANTIFICATION: "quantification",
  NORMATIVEGRAPH: "normative-graph",
  LONGITUDINALGRAPH: "longitudinal-graph",
  MICROBLEED: "microbleed-evolution",
  RENDERING3D: "3d-rendering",
} as const;

const CORTICAL_LOBES_RID = "RID13498";

// Capability flags describe what a study CAN do — a fixed property of its data.
// They're derived from segmentsPerSeries / populatedMarkers, which get cleared
// and repopulated when the viewer re-discovers (e.g. on mode switch). To stop
// the mode menu from collapsing during those transient empty states, we latch
// the flags per study+acquisition: once true, they stay true until the
// acquisition changes (at which point the new exam is evaluated fresh).
type Caps = {
  isLongitudinal: boolean;
  hasSomeNormative: boolean;
  hasLobeNormativeData: boolean;
  isMicrobleed: boolean;
};
const capsCache = new Map<string, Caps>();

function Modes() {
  const t = useTranslate();
  const { getViewerPath, atViewerPage, study_id, acquisition, examDate } =
    useViewerParams();
  const { populatedMarkers } = useMarkers(study_id);
  const { segmentsPerSeries, niftiBases } = useSnapshot(viewerState);

  const viewerPath = getViewerPath();

  // ── Raw flags from the current (volatile) viewer state ──────────────────
  const rawIsLongitudinal = segmentsPerSeries.some((seg) => {
    const l = seg.SegmentLabel?.toLowerCase() ?? "";
    return (
      l.includes("increased") ||
      l.includes("decreased") ||
      l.includes("stable") ||
      l.includes("static")
    );
  });

  const rawHasSomeNormative = Boolean(
    populatedMarkers?.some((m) => m.percentileRange),
  );

  const lobesMarkers = markers
    ?.find((m) => m.code === CORTICAL_LOBES_RID)
    ?.substructures?.map((rid) => populatedMarkers.find((m) => m.code === rid));
  const rawHasLobeNormativeData = Boolean(
    lobesMarkers?.length && lobesMarkers.every((m) => m?.percentileRange),
  );

  const rawIsMicrobleed = Boolean((niftiBases as any)?.t2starmri);

  // ── Latch per study+acquisition: never downgrade true → false ───────────
  const cacheKey = `${study_id ?? ""}::${acquisition ?? ""}`;
  const prev = capsCache.get(cacheKey) ?? {
    isLongitudinal: false,
    hasSomeNormative: false,
    hasLobeNormativeData: false,
    isMicrobleed: false,
  };
  const caps: Caps = {
    isLongitudinal: prev.isLongitudinal || rawIsLongitudinal,
    hasSomeNormative: prev.hasSomeNormative || rawHasSomeNormative,
    hasLobeNormativeData: prev.hasLobeNormativeData || rawHasLobeNormativeData,
    isMicrobleed: prev.isMicrobleed || rawIsMicrobleed,
  };
  capsCache.set(cacheKey, caps);

  const {
    isLongitudinal,
    hasSomeNormative,
    hasLobeNormativeData,
    isMicrobleed,
  } = caps;

  if (!atViewerPage) {
    return null;
  }

  return (
    <div>
      <NavLink
        to={`${viewerPath}/${MODES.QUANTIFICATION}?exam_date=${examDate}`}
        data-testid={`viewer-menu-mode-button-${MODES.QUANTIFICATION}`}
        className="block text-center p-2 text-lg my-1 mx-3 bg-stone-700 rounded"
      >
        {t("modes.quantification")}
      </NavLink>
      {hasSomeNormative ? (
        <NavLink
          to={`${viewerPath}/${MODES.NORMATIVEGRAPH}?exam_date=${examDate}`}
          data-testid={`viewer-menu-mode-button-${MODES.NORMATIVEGRAPH}`}
          className="block text-center p-2 text-lg my-1 mx-3 bg-stone-700 rounded"
        >
          {t("modes.normative_graph")}
        </NavLink>
      ) : null}
      {hasLobeNormativeData ? (
        <NavLink
          to={`${viewerPath}/${MODES.RENDERING3D}?exam_date=${examDate}`}
          data-testid={`viewer-menu-mode-button-${MODES.RENDERING3D}`}
          className="block text-center p-2 text-lg my-1 mx-3 bg-stone-700 rounded"
        >
          {t("modes.3d_rendering")}
        </NavLink>
      ) : null}
      {isLongitudinal ? (
        <NavLink
          to={`${viewerPath}/${MODES.LONGITUDINALGRAPH}?exam_date=${examDate}`}
          data-testid={`viewer-menu-mode-button-${MODES.LONGITUDINALGRAPH}`}
          className="block text-center p-2 text-lg my-1 mx-3 bg-stone-700 rounded"
        >
          {t("modes.longitudinal")}
        </NavLink>
      ) : null}
      {isMicrobleed ? (
        <>
          <NavLink
            to={`${viewerPath}/${MODES.MICROBLEED}?exam_date=${examDate}`}
            data-testid={`viewer-menu-mode-button-${MODES.MICROBLEED}`}
            className="block text-center p-2 text-lg my-1 mx-3 bg-stone-700 rounded"
          >
            {t("modes.microbleed")}
          </NavLink>
        </>
      ) : null}
    </div>
  );
}

export default Modes;
